import { TranslateService } from '@ngx-translate/core';

import { CmpsDatePipe } from '../../common/date.pipe';
import { CmpsDecimalPipe, CmpsPercentPipe } from '../../common/number.pipe';
import { Exporter } from '../../insight/experimental.grid-export';
import { PayloadAction } from '../../store.model';
import { GridType, WidgetType } from '../core/template-definition.model';
import { CmpsNumberWithSign } from '../numberWithSign.pipe';
import { Scorecard } from '../scorecard-next.model';
import { TranslationKeyMap } from '../scorecard.i18n';
import {
    CompensationBonus,
    CompensationCondition,
    CompensationModifier,
    CompensationQualifier,
    FyiMetric,
    OverallScore,
    Rank,
} from '../scorecard.model';
import { CompensationDescriptionPipe } from '../widgets/compensation-description.pipe';
import { GridEpicBridge } from '../widgets/grid/grid.epic.bridge';
import { exportGetValue } from '../widgets/grid/grid.model';
import {
    exportContributionGetValue,
    exportRankGetValue,
    exportScorecardGetValue,
} from '../widgets/grid/team-grid.model';

import { GridTab } from './grid-tab.model';
import { ActionType, WidgetsExport } from './scorecard.action';
import { ScorecardState } from './scorecard.state';

const _i18n = TranslationKeyMap;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function exportMultipleWidget(
    act: PayloadAction<ActionType, WidgetsExport>,
    rootState: ScorecardState,
    exp: Exporter,
    bridge: GridEpicBridge,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
    datePipe: CmpsDatePipe,
    numberWithSignPipe: CmpsNumberWithSign,
    compDescPipe: CompensationDescriptionPipe,
) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const ts = (key: string): string | any => translateService.instant(key);
    const sc: Scorecard = rootState.selectedTab.selectedScorecard;
    const widgets = sc.data.templateConfig.widgets;
    const layouts = [];
    sc.data.templateConfig.layouts.forEach((e) => {
        if (e.itemType === 'widget') {
            layouts.push(e);
        } else {
            // container not widget
            e.children.forEach((c) => {
                if (c.itemType === 'container') {
                    c.children.forEach((w) => {
                        if (w.itemType === 'widget') {
                            layouts.push(w);
                        }
                    });
                } else {
                    layouts.push(c);
                }
            });
        }
    });
    layouts.sort((a, b) => a.displayOrder - b.displayOrder);
    if (rootState.selectedTab.selectedGridTab === GridTab.SCORECARD_GRID) {
        layouts.forEach((e) => {
            const widget = widgets.find(
                (w) => w.identifier === e.identifier && w.enabled === true,
            );
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            switch (widget && widget.widgetType) {
                case WidgetType.SCORECARD_GRID:
                    exportPerformanceGrid(
                        rootState,
                        exp,
                        bridge,
                        translateService,
                        decimalPipe,
                        percentPipe,
                        datePipe,
                        numberWithSignPipe,
                    );
                    break;
                case WidgetType.FYI_GRID:
                    exportFyi(
                        rootState,
                        exp,
                        translateService,
                        decimalPipe,
                        percentPipe,
                    );
                    break;
                case WidgetType.OVERALL_SCORE_GRID:
                    exportOverall(
                        rootState,
                        exp,
                        translateService,
                        decimalPipe,
                    );
                    break;
                case WidgetType.RANKINGS_GRID:
                    exportRanks(rootState, exp, translateService);
                    break;
                case WidgetType.COMPENSATION_CONDITION:
                    exportCompCondition(
                        rootState,
                        exp,
                        translateService,
                        decimalPipe,
                        compDescPipe,
                    );
                    break;
                case WidgetType.COMPENSATION_QUALIFIERS:
                    exportComQualifiers(
                        rootState,
                        exp,
                        translateService,
                        decimalPipe,
                        compDescPipe,
                    );
                    break;
                case WidgetType.COMPENSATION_MODIFIERS:
                    exportComModifiers(
                        rootState,
                        exp,
                        translateService,
                        decimalPipe,
                        compDescPipe,
                    );
                    break;
                case WidgetType.COMPENSATION_BONUS:
                    exportCompBonus(
                        rootState,
                        exp,
                        translateService,
                        decimalPipe,
                    );
                    break;
            }
        });
    } else {
        switch (act.payload.activeDrptTab) {
            case GridType.Rank_View:
                exportDrtRankGrid(
                    rootState,
                    exp,
                    bridge,
                    translateService,
                    decimalPipe,
                    percentPipe,
                    datePipe,
                    numberWithSignPipe,
                );
                break;
            case GridType.Contribution_View:
                exportDrtContributionGrid(
                    rootState,
                    exp,
                    bridge,
                    translateService,
                    decimalPipe,
                );
                break;
            case GridType.Direct_Reports_View:
                exportDrtReportGrid(
                    rootState,
                    exp,
                    bridge,
                    translateService,
                    decimalPipe,
                    percentPipe,
                );
                break;
        }
    }
}

// export performance grid
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportPerformanceGrid(
    rootState: ScorecardState,
    exp: Exporter,
    bridge: GridEpicBridge,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
    datePipe: CmpsDatePipe,
    numberWithSignPipe: CmpsNumberWithSign,
) {
    exp.addWorksheetWithName('performance');
    const ts = (key: string): string | any => translateService.instant(key);
    const columnMerge: number[] = [0];
    const publishedKey = rootState.selectedTab.selectedScorecard.header
        .published
        ? _i18n._9113_PUBLISHED
        : _i18n._9114_UNPUBLISHED;
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._102_USER_NAME)}: ${rootState.selectedTab.user.firstName} ${
            rootState.selectedTab.user.lastName
        }`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._804_SUPERGROUP_)} ${
            rootState.selectedTab.selectedScorecard.header.groupName
        }`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._773_PERIOD)} ${ts(_i18n._9250_FROM)} ${datePipe.transform(
            rootState.selectedTab.selectedScorecard.header.startDate,
            'MM-dd-y',
        )}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._773_PERIOD)} ${ts(_i18n._9253_TO)} ${datePipe.transform(
            rootState.selectedTab.selectedScorecard.header.endDate,
            'MM-dd-y',
        )}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9351_SCORECARD_ID)}: ${
            rootState.selectedTab.selectedScorecard.header.scorecardId
        }`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9352_SCORECARD_STATUS)}: ${ts(publishedKey)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9388_GENERATED_ON)}: ${datePipe.transform(
            rootState.selectedTab.selectedScorecard.header.generatedOn,
            'MM-dd-y h:mm:ss a',
        )}`,
    ];
    exp.writeRow(headerRow);
    exp.writeRow([]);
    exp.writeGridWorkbook(
        bridge.renderer_tab_grid as any,
        (l) => {
            return exportGetValue(
                l,
                translateService,
                decimalPipe,
                percentPipe,
                datePipe,
                numberWithSignPipe,
            );
        },
        () => {
            return false;
        },
        columnMerge,
    );
}

// export direct rank grid
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportDrtRankGrid(
    rootState: ScorecardState,
    exp: Exporter,
    bridge: GridEpicBridge,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
    datePipe: CmpsDatePipe,
    numberWithSignPipe: CmpsNumberWithSign,
) {
    exp.addWorksheetWithName('Rank');
    const ts = (key: string): string | any => translateService.instant(key);
    const columnMerge: number[] = [0];
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9351_SCORECARD_ID)}: ${
            rootState.selectedTab.selectedScorecard.header.scorecardId
        }`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._102_USER_NAME)}: ${rootState.selectedTab.user.firstName} ${
            rootState.selectedTab.user.lastName
        }`,
    ];
    exp.writeRow(headerRow);
    exp.writeRow([]);
    exp.writeGridWorkbook(
        bridge.renderer_direct_reports as any,
        (l) => {
            return exportRankGetValue(
                l,
                translateService,
                decimalPipe,
                percentPipe,
                datePipe,
                numberWithSignPipe,
            );
        },
        () => {
            return false;
        },
        columnMerge,
    );
}

// export direct contribution grid
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportDrtContributionGrid(
    rootState: ScorecardState,
    exp: Exporter,
    bridge: GridEpicBridge,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
) {
    exp.addWorksheetWithName('contribution');
    const ts = (key: string): string | any => translateService.instant(key);
    const columnMerge: number[] = [0];
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9351_SCORECARD_ID)}: ${
            rootState.selectedTab.selectedScorecard.header.scorecardId
        }`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._102_USER_NAME)}: ${rootState.selectedTab.user.firstName} ${
            rootState.selectedTab.user.lastName
        }`,
    ];
    exp.writeRow(headerRow);
    exp.writeRow([]);
    exp.writeGridWorkbook(
        bridge.renderer_direct_reports as any,
        (l) => {
            return exportContributionGetValue(l, translateService, decimalPipe);
        },
        () => {
            return false;
        },
        columnMerge,
    );
}

// export direct report grid
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportDrtReportGrid(
    rootState: ScorecardState,
    exp: Exporter,
    bridge: GridEpicBridge,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
) {
    const ts = (key: string): string | any => translateService.instant(key);
    const columnMerge: number[] = [0];
    exp.addWorksheetWithName('direct_reports');
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9351_SCORECARD_ID)}: ${
            rootState.selectedTab.selectedScorecard.header.scorecardId
        }`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._102_USER_NAME)}: ${rootState.selectedTab.user.firstName} ${
            rootState.selectedTab.user.lastName
        }`,
    ];
    exp.writeRow(headerRow);
    exp.writeRow([]);
    exp.writeGridWorkbook(
        bridge.renderer_direct_reports as any,
        (l) => {
            return exportScorecardGetValue(
                l,
                translateService,
                decimalPipe,
                percentPipe,
            );
        },
        () => {
            return false;
        },
        columnMerge,
    );
}

// export fyi widget
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportFyi(
    rootState: ScorecardState,
    exp: Exporter,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
) {
    exp.addWorksheetWithName('fyi');
    const ts = (key: string): string | any => translateService.instant(key);
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._7146_METRIC_NAME)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._725_ACTUAL)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._726_TARGET)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._768___TO_TARGET)}`,
    ];
    const fyiMetrics = rootState.selectedTab.selectedScorecard.data.fyiMetrics;
    if (fyiMetrics.length === 0) return;
    exp.writeRow(headerRow);
    fyiMetrics.forEach((d) => {
        const dataRow = exportFyiGetValue(
            d,
            translateService,
            decimalPipe,
            percentPipe,
        );
        exp.writeRow(dataRow);
    });
}

// export overall widget
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportOverall(
    rootState: ScorecardState,
    exp: Exporter,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
) {
    exp.addWorksheetWithName('overall');
    const overallScores =
        rootState.selectedTab.selectedScorecard.data.overallScores;
    overallScores.forEach((d) => {
        const dataRow = exportOverallGetValue(d, translateService, decimalPipe);
        exp.writeRow(dataRow);
    });
}

// export ranks widget
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportRanks(
    rootState: ScorecardState,
    exp: Exporter,
    translateService: TranslateService,
) {
    exp.addWorksheetWithName('ranks');
    const ranks = rootState.selectedTab.selectedScorecard.data.ranks;
    ranks.forEach((d) => {
        const dataRow = exportRankWidgetValue(d, translateService);
        exp.writeRow(dataRow);
    });
}

// export compensation condition widget
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportCompCondition(
    rootState: ScorecardState,
    exp: Exporter,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    compDescPipe: CompensationDescriptionPipe,
) {
    exp.addWorksheetWithName('condition');
    const ts = (key: string): string | any => translateService.instant(key);
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9301_COMPENSATION_DESCRIPTION)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9302_COMPENSATION_CURRENT_VALUE)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9303_COMPENSATION_IN_EFFECT)}`,
    ];
    const conditions =
        rootState.selectedTab.selectedScorecard.data.compensationConditions;
    if (conditions.length === 0) return;
    exp.writeRow(headerRow);
    conditions.forEach((d) => {
        const dataRow = exportConditionGetValue(
            d,
            translateService,
            decimalPipe,
            compDescPipe,
        );
        exp.writeRow(dataRow);
    });
}

// export compensation qualifiers widget
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportComQualifiers(
    rootState: ScorecardState,
    exp: Exporter,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    compDescPipe: CompensationDescriptionPipe,
) {
    const ts = (key: string): string | any => translateService.instant(key);
    exp.addWorksheetWithName('qualifiers');
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9301_COMPENSATION_DESCRIPTION)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9302_COMPENSATION_CURRENT_VALUE)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9374_IN_EFFECT)}`,
    ];
    const qualifiers =
        rootState.selectedTab.selectedScorecard.data.compensationQualifiers;
    if (qualifiers.length === 0) return;
    exp.writeRow(headerRow);
    qualifiers.forEach((d) => {
        const dataRow = exportQualifierGetValue(
            d,
            translateService,
            decimalPipe,
            compDescPipe,
        );
        exp.writeRow(dataRow);
    });
}

// export compensation modifiers
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportComModifiers(
    rootState: ScorecardState,
    exp: Exporter,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    compDescPipe: CompensationDescriptionPipe,
) {
    exp.addWorksheetWithName('modifier');
    const ts = (key: string): string | any => translateService.instant(key);
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9301_COMPENSATION_DESCRIPTION)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9302_COMPENSATION_CURRENT_VALUE)}`,
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9303_COMPENSATION_IN_EFFECT)}`,
    ];
    const modifiers =
        rootState.selectedTab.selectedScorecard.data.compensationModifiers;
    if (modifiers.length === 0) return;
    exp.writeRow(headerRow);
    modifiers.forEach((d) => {
        const dataRow = exportModifierGetValue(
            d,
            translateService,
            decimalPipe,
            compDescPipe,
        );
        exp.writeRow(dataRow);
    });
}

// export compensation bonus
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function exportCompBonus(
    rootState: ScorecardState,
    exp: Exporter,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
) {
    exp.addWorksheetWithName('bonus');
    const ts = (key: string): string | any => translateService.instant(key);
    const headerRow = [
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `${ts(_i18n._9308_ESTIMATED_PAYOUT)}`,
    ];
    exp.writeRow(headerRow);
    const bonus =
        rootState.selectedTab.selectedScorecard.data.compensationBonus;
    const dataRow = exportBonusGetValue(bonus, decimalPipe);
    exp.writeRow(dataRow);
}

function exportFyiGetValue(
    data: FyiMetric,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    percentPipe: CmpsPercentPipe,
): any {
    const result = [];
    const metricName = !data.metricName
        ? '-'
        : translateService.instant(data.metricName);
    result.push(metricName);

    const actual =
        typeof data.actual !== 'number'
            ? '-'
            : decimalPipe.transform(data.actual, '1.0-2');
    result.push(actual);

    const target =
        typeof data.target !== 'number'
            ? '-'
            : decimalPipe.transform(data.target, '1.0-2');
    result.push(target);

    const targetPercentage =
        typeof data.targetPercentage !== 'number'
            ? '-'
            : percentPipe.transform(data.targetPercentage, '1.0-2');
    result.push(targetPercentage);

    return result;
}

function exportOverallGetValue(
    data: OverallScore,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
): any {
    const result = [];
    const scoreText = !data.scoreText
        ? '-'
        : translateService.instant(data.scoreText);
    result.push(scoreText);

    const scoreValue =
        typeof data.scoreValue !== 'number'
            ? '-'
            : decimalPipe.transform(data.scoreValue, '1.0-2');
    result.push(scoreValue);

    return result;
}

function exportRankWidgetValue(
    data: Rank,
    translateService: TranslateService,
): any {
    const result = [];
    const rankName = !data.rankName
        ? '-'
        : translateService.instant(data.rankName);
    result.push(rankName);

    const rankVal =
        !data.rankResultScorecardPeriod || !data.totalPositionsScorecardPeriod
            ? '-'
            : `${data.rankResultScorecardPeriod}/${data.totalPositionsScorecardPeriod}`;
    result.push(rankVal);

    return result;
}

function exportConditionGetValue(
    data: CompensationCondition,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    compDescPipe: CompensationDescriptionPipe,
): any {
    const ts = (key: string): string | any => translateService.instant(key);
    const result = [];
    const description = !data.description
        ? '-'
        : compDescPipe.transform(data.description, data);
    result.push(description);

    const value =
        typeof data.value !== 'number'
            ? '-'
            : decimalPipe.transform(data.value, '1.0-2');
    result.push(value);

    const isInEffect = !data.inEffect
        ? ts(_i18n._9298_NO)
        : ts(_i18n._9297_YES);
    result.push(isInEffect);

    return result;
}

function exportQualifierGetValue(
    data: CompensationQualifier,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    compDescPipe: CompensationDescriptionPipe,
): any {
    const ts = (key: string): string | any => translateService.instant(key);
    const result = [];
    const description = !data.description
        ? '-'
        : compDescPipe.transform(data.description, data);
    result.push(description);

    const value =
        typeof data.value !== 'number'
            ? '-'
            : decimalPipe.transform(data.value, '1.0-2');
    result.push(value);

    const qualifierMet = !data.qualifierMet
        ? ts(_i18n._9298_NO)
        : ts(_i18n._9297_YES);
    result.push(qualifierMet);

    return result;
}

function exportModifierGetValue(
    data: CompensationModifier,
    translateService: TranslateService,
    decimalPipe: CmpsDecimalPipe,
    compDescPipe: CompensationDescriptionPipe,
): any {
    const ts = (key: string): string | any => translateService.instant(key);
    const result = [];
    const description = !data.description
        ? '-'
        : compDescPipe.transform(data.description, data);
    result.push(description);

    const value =
        typeof data.value !== 'number'
            ? '-'
            : decimalPipe.transform(data.value, '1.0-2');
    result.push(value);

    const isInEffect = !data.inEffect
        ? ts(_i18n._9298_NO)
        : ts(_i18n._9297_YES);
    result.push(isInEffect);

    return result;
}

function exportBonusGetValue(
    data: CompensationBonus,
    decimalPipe: CmpsDecimalPipe,
): any {
    const result = [];
    const bonusAmount =
        typeof data.bonusAmount !== 'number'
            ? '-'
            : decimalPipe.transform(data.bonusAmount, '1.0-2');
    result.push(bonusAmount);

    return result;
}
